import { useEffect, useState, useCallback } from "react";
import { api, errorMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UsuarioFormDialog } from "@/components/UsuarioFormDialog";
import { ResetPasswordDialog } from "@/components/ResetPasswordDialog";
import { Plus, Pencil, Trash2, Users, KeyRound } from "lucide-react";

export default function Usuarios() {
  const { user } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [dialog, setDialog] = useState({ open: false, usuario: null });
  const [resetDialog, setResetDialog] = useState({ open: false, usuario: null });

  const cargar = useCallback(async () => setUsuarios((await api.get("/usuarios")).data), []);
  useEffect(() => { cargar(); }, [cargar]);

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar este usuario?")) return;
    try { await api.delete(`/usuarios/${id}`); toast.success("Usuario eliminado"); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const toggleActivo = async (u) => {
    try { await api.patch(`/usuarios/${u.id}`, { activo: !u.activo }); cargar(); }
    catch (e) { toast.error(errorMsg(e)); }
  };

  const abrir = (u) => setDialog({ open: true, usuario: u });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2"><Users size={22} /> Administración de usuarios</h1>
          <p className="text-sm text-muted-foreground">{usuarios.length} usuarios</p>
        </div>
        <Button data-testid="nuevo-usuario-btn" onClick={() => abrir(null)}><Plus size={15} className="mr-1" /> Nuevo usuario</Button>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary">
              <TableHead>Usuario</TableHead>
              <TableHead className="hidden sm:table-cell">Nombre</TableHead>
              <TableHead className="hidden md:table-cell">Teléfono</TableHead>
              <TableHead>Rol</TableHead>
              <TableHead>Estado</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {usuarios.map((u) => (
              <TableRow key={u.id} data-testid={`usuario-row-${u.usuario}`}>
                <TableCell className="font-medium">{u.usuario}</TableCell>
                <TableCell className="hidden sm:table-cell text-muted-foreground">{u.nombre}</TableCell>
                <TableCell className="hidden md:table-cell text-muted-foreground">{u.telefono || "—"}</TableCell>
                <TableCell>
                  <span data-testid={`usuario-rol-badge-${u.usuario}`} className={`text-xs px-2 py-0.5 rounded-sm font-semibold uppercase ${u.rol === "admin" ? "bg-primary text-white" : "bg-secondary text-foreground"}`}>
                    {u.rol_nombre || (u.rol === "admin" ? "Administrador" : "Sin rol")}
                  </span>
                </TableCell>
                <TableCell>
                  <button data-testid={`toggle-activo-${u.usuario}`} onClick={() => toggleActivo(u)} disabled={u.id === user.id}
                    className={`text-xs px-2 py-0.5 rounded-sm font-medium ${u.activo ? "bg-green-100 text-green-800" : "bg-zinc-200 text-zinc-600"}`}>
                    {u.activo ? "Activo" : "Inactivo"}
                  </button>
                  {u.debe_cambiar_password && (
                    <span data-testid={`badge-debe-cambiar-${u.usuario}`}
                      className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 font-semibold uppercase">
                      Debe cambiar clave
                    </span>
                  )}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button data-testid={`editar-usuario-${u.usuario}`} variant="ghost" size="icon" className="h-7 w-7" onClick={() => abrir(u)}><Pencil size={14} /></Button>
                    {u.id !== user.id && (
                      <Button data-testid={`reset-password-usuario-${u.usuario}`} variant="ghost" size="icon"
                        className="h-7 w-7 text-amber-600 hover:text-amber-700" title="Resetear contraseña"
                        onClick={() => setResetDialog({ open: true, usuario: u })}><KeyRound size={14} /></Button>
                    )}
                    {u.id !== user.id && (
                      <Button data-testid={`eliminar-usuario-${u.usuario}`} variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => eliminar(u.id)}><Trash2 size={14} /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <UsuarioFormDialog open={dialog.open} usuario={dialog.usuario}
        onOpenChange={(v) => setDialog({ open: v, usuario: v ? dialog.usuario : null })} onSaved={cargar} />
      <ResetPasswordDialog open={resetDialog.open} usuario={resetDialog.usuario}
        onOpenChange={(v) => { setResetDialog({ open: v, usuario: v ? resetDialog.usuario : null }); if (!v) cargar(); }} />
    </div>
  );
}
